<div class="modal-header shadow-sm">
  <h5 class="modal-title fw-bold">{{ isEdit ? 'Edit' : 'Add' }} Threat Pair</h5>
  <button
    type="button"
    class="btn-close"
    aria-label="Close"
    [disabled]="loading"
    (click)="close()"
  ></button>
</div>

<div class="modal-body scrollbar-primary thin pt-3">
  <form [formGroup]="form" (ngSubmit)="save()" autocomplete="off" id="addEditThreatPairForm">
    <div class="mb-2">
      <label for="organizationId" class="form-label">Organization <span class="text-danger">*</span></label>
      <app-select
        [form]="form"
        [control]="form.controls['organizationId']"
        [controlName]="'organizationId'"
        [data]="organizationList"
        [name]="'label'"
        [value]="'value'"
        [submitted]="submitted"
        [required]="true"
        [disabled]="isEdit || organizationSelectLocked"
        [placeholder]="'Select organization...'"
        [width]="'100%'"
        (emitSelectedValue)="onOrganizationChange($event)"
      ></app-select>
    </div>

    @if (showViewAsAdvocateSection) {
      <div class="form-check mb-2">
        <input
          type="checkbox"
          class="form-check-input"
          id="viewAsAdvocate"
          />
        <label class="form-check-label" for="viewAsAdvocate">View as Advocate (org read-only).</label>
      </div>
      <p class="small text-muted mb-3">
        Only survivors and perpetrators from the selected organization appear in the lists below.
        For Advocate users, Organization is read-only and shows their assigned organization.
      </p>
    }
    <div class="row gx-3 mb-2">
      <div class="col-6">
        <label for="survivorId" class="form-label">
          Select Survivor <span class="text-danger">*</span>
        </label>
        <app-select
          class="select-outline"
          [form]="form"
          [control]="form.controls['survivorId']"
          [controlName]="'survivorId'"
          [data]="survivorUserList"
          [name]="'label'"
          [value]="'value'"
          [submitted]="submitted"
          [required]="true"
          [disabled]="isEdit"
          [placeholder]="'Select your option'"
          [width]="'100%'"
        ></app-select>
      </div>
      <div class="col-6">
        <label for="perpetratorIds" class="form-label">
          Select perpetrators <span class="text-danger">*</span>
        </label>
        <app-select
          class="select-outline"
          [form]="form"
          [control]="form.controls['perpetratorIds']"
          [controlName]="'perpetratorIds'"
          [data]="perpetratorUserList"
          [name]="'label'"
          [value]="'value'"
          [submitted]="submitted"
          [required]="true"
          [multiple]="true"
          [placeholder]="'Select one or more..'"
          [width]="'100%'"
          [disabled]="isEdit"
        ></app-select>
      </div>
    </div>

    <div class="mb-2">
      <h6 class="mb-1 fw-normal">Distance Threshold <span class="text-danger">*</span></h6>
      <div class="mb-2">
        <label class="form-label mb-1">Select your preferred unit for measuring distance.</label>
        <div class="d-flex flex-wrap gap-3">
          @for (option of distanceParameterOptions; track option.value) {
            <div class="form-check">
              <input
                type="checkbox"
                class="form-check-input"
                id="distanceParameter-{{ option.value }}"
                [checked]="form.get('distanceParameter')?.value === option.value"
                (change)="onDistanceParameterChange(option.value)"
                />
              <label
                class="form-check-label"
                for="distanceParameter-{{ option.value }}"
              >{{ option.label }}</label>
            </div>
          }
        </div>
        @if (submitted && form.controls['distanceParameter'].invalid) {
          <div class="text-danger small mt-1">Distance parameter is required.</div>
        }
      </div>
      <div class="row gx-3">
        <!-- Yellow Zone -->
        <div class="col-6">
          <div class="card mb-0 border-0 bg-accent-2">
            <div class="card-body p-2">
              <div class="d-flex align-items-center gap-2 mb-1">
                <span
                  class="nav-link avatar avatar-with-icon p-0 bg-warning text-center text-white d-inline-flex align-items-center justify-content-center"
                >
                  <i class="fas fa-exclamation-triangle"></i>
                </span>
                <p class="mb-0 fw-bold text-yellow-warning">Yellow Zone</p>
              </div>
              <div class="row gx-2">
                <div class="col-8">
                  <label for="distanceThreshold" class="form-label">Early warning perimeter</label>
                  <div class="input-group">
                    <input
                      type="number"
                      class="form-control bg-gray-lighter no-stepper"
                      id="distanceThreshold"
                      formControlName="distanceThreshold"
                      min="0.1"
                      placeholder="{{ defaultYellowDistanceThreshold }}"
                      [class.is-invalid]="
                        submitted && form.controls['distanceThreshold'].invalid
                      "
                      />
                    <span class="input-group-text bg-light">{{ selectedDistanceParameterLabel }}</span>
                  </div>
                </div>
                <div class="col-4">
                  <label for="yellowCheckInTime" class="form-label">Check-in time</label>
                  <div class="input-group">
                    <input
                      type="number"
                      class="form-control bg-gray-lighter no-stepper"
                      id="yellowCheckInTime"
                      formControlName="yellowCheckInTime"
                      min="10"
                      step="10"
                      readonly
                      placeholder="{{ defaultYellowCheckInTime }}"
                      [class.is-invalid]="
                        submitted && form.controls['yellowCheckInTime'].invalid
                      "
                      />
                    <span class="input-group-text bg-light">s</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Red Zone -->
        <div class="col-6">
          <div class="card mb-0 border-0 bg-red-bg-color">
            <div class="card-body p-2">
              <div class="d-flex align-items-center gap-2 mb-1">
                <span
                  class="nav-link avatar avatar-with-icon p-0 bg-warning-bg-color text-center text-white d-inline-flex align-items-center justify-content-center"
                >
                  <i class="fas fa-exclamation-triangle"></i>
                </span>
                <p class="mb-0 fw-bold text-red-danger">Red Zone</p>
              </div>
              <div class="row gx-2">
                <div class="col-8">
                  <label for="redDistanceThreshold" class="form-label">Critical warning perimeter</label>
                  <div class="input-group">
                    <input
                      type="number"
                      class="form-control bg-gray-lighter no-stepper"
                      id="redDistanceThreshold"
                      formControlName="redDistanceThreshold"
                      min="0.1"
                      placeholder="{{ defaultRedDistanceThreshold }}"
                      [class.is-invalid]="
                        submitted && form.controls['redDistanceThreshold'].invalid
                      "
                      />
                    <span class="input-group-text bg-light">{{ selectedDistanceParameterLabel }}</span>
                  </div>
                </div>
                <div class="col-4">
                  <label for="redCheckInTime" class="form-label">Check-in time</label>
                  <div class="input-group">
                    <input
                      type="number"
                      class="form-control bg-gray-lighter no-stepper"
                      id="redCheckInTime"
                      formControlName="redCheckInTime"
                      min="10"
                      step="10"
                      readonly
                      placeholder="{{ defaultRedCheckInTime }}"
                      [class.is-invalid]="
                        submitted && form.controls['redCheckInTime'].invalid
                      "
                      />
                    <span class="input-group-text bg-light">s</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      @if (form.hasError('yellowGreaterThanRed')) {
        <div class="text-danger small mt-1">
          Yellow Zone perimeter must be greater than Red Zone perimeter.
        </div>
      }
    </div>

    <div class="mb-0">
      <h6 class="mb-1 fw-normal">Notification Channels</h6>
      <p class="small text-muted mb-2">Alerts are sent to the survivor. Push notification is required.</p>
      <div class="d-flex flex-wrap gap-3">
        <div class="form-check">
          <input
            type="checkbox"
            class="form-check-input"
            id="notificationPush"
            formControlName="notificationPush"
            />
          <label class="form-check-label" for="notificationPush">Push</label>
        </div>
        <div class="form-check">
          <input
            type="checkbox"
            class="form-check-input"
            id="notificationEmail"
            formControlName="notificationEmail"
            />
          <label class="form-check-label" for="notificationEmail">Email</label>
        </div>
        <div class="form-check">
          <input
            type="checkbox"
            class="form-check-input"
            id="notificationSms"
            formControlName="notificationSms"
            />
          <label class="form-check-label" for="notificationSms">SMS</label>
        </div>
      </div>
    </div>
  </form>
</div>

<div class="modal-footer d-flex justify-content-between gap-2 pt-0 border-0 bg-gray-lighter">
  <button
    type="button"
    class="btn btn-light border"
    [disabled]="loading"
    (click)="close()"
    >
    Cancel
  </button>
  <button
    type="submit"
    class="btn btn-primary"
    form="addEditThreatPairForm"
    [disabled]="form.invalid || loading"
    >
    @if (loading) {
      <span
        class="spinner-grow spinner-grow-sm"
        role="status"
        aria-hidden="true"
      ></span>
    }
    Save
  </button>
</div>
